import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

export const CalendarView = ({ events, view = 'month', onViewChange, onMonthChange, initialMonth, initialYear }) => {
  const [currentDate, setCurrentDate] = useState(() => {
    const date = new Date();
    if (initialMonth && initialYear) {
      date.setMonth(initialMonth - 1);
      date.setFullYear(initialYear);
    }
    return date;
  });
  const [calendarDays, setCalendarDays] = useState([]);

  useEffect(() => {
    generateCalendarDays();
  }, [currentDate]);

  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    let startPadding = firstDay.getDay() - 1;
    if (startPadding < 0) startPadding = 6;
    
    const days = [];
    
    const prevMonth = new Date(year, month, 0);
    for (let i = startPadding - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false
      });
    }
    
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    
    setCalendarDays(days);
  };

  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events[dateStr] || [];
  };

  const formatDateStr = (date) => {
    return date.toISOString().split('T')[0];
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      
      // Notify parent of month change
      if (onMonthChange) {
        onMonthChange(newDate.getMonth() + 1, newDate.getFullYear());
      }
      
      return newDate;
    });
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    if (onMonthChange) {
      onMonthChange(today.getMonth() + 1, today.getFullYear());
    }
  };

  const getWeekDays = () => {
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    startOfWeek.setDate(startOfWeek.getDate() + diff);
    
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      weekDays.push(date);
    }
    return weekDays;
  };

  const navigateWeek = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(prev.getDate() + (direction * 7));
      
      if (onMonthChange) {
        onMonthChange(newDate.getMonth() + 1, newDate.getFullYear());
      }
      
      return newDate;
    });
  };

  const genreColors = {
    opera: 'bg-red-500/20 text-red-300 border-red-500/30',
    ballet: 'bg-pink-500/20 text-pink-300 border-pink-500/30',
    concierto: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    teatro: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
    musical: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    cine: 'bg-green-500/20 text-green-300 border-green-500/30'
  };

  return (
    <div className="calendar-container p-6" data-testid="calendar-view">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => view === 'week' ? navigateWeek(-1) : navigateMonth(-1)}
            className="p-2 text-white/50 hover:text-white transition-colors"
            data-testid="calendar-prev"
          >
            <ChevronLeft size={24} />
          </button>
          <h2 className="font-['Playfair_Display'] text-2xl text-white min-w-[200px] text-center">
            {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          <button
            onClick={() => view === 'week' ? navigateWeek(1) : navigateMonth(1)}
            className="p-2 text-white/50 hover:text-white transition-colors"
            data-testid="calendar-next"
          >
            <ChevronRight size={24} />
          </button>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={goToToday}
            className="btn-outline text-xs"
            data-testid="calendar-today"
          >
            Hoy
          </button>
          <div className="flex border border-white/10">
            <button
              onClick={() => onViewChange('month')}
              className={`view-toggle ${view === 'month' ? 'active' : ''}`}
              data-testid="view-month"
            >
              Mes
            </button>
            <button
              onClick={() => onViewChange('week')}
              className={`view-toggle ${view === 'week' ? 'active' : ''}`}
              data-testid="view-week"
            >
              Semana
            </button>
          </div>
        </div>
      </div>

      {/* Month View */}
      {view === 'month' && (
        <div>
          <div className="grid grid-cols-7 mb-2">
            {DAYS.map(day => (
              <div key={day} className="text-center py-3 text-xs uppercase tracking-widest text-white/50">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {calendarDays.map((day, index) => {
              const dayEvents = getEventsForDate(day.date);
              const hasEvents = dayEvents.length > 0;
              
              return (
                <div
                  key={index}
                  className={`calendar-day p-2 min-h-[120px] ${
                    !day.isCurrentMonth ? 'opacity-30' : ''
                  } ${hasEvents ? 'has-events' : ''} ${
                    isToday(day.date) ? 'bg-[var(--primary)]/10 border-[var(--primary)]/30' : ''
                  }`}
                  data-testid={`calendar-day-${formatDateStr(day.date)}`}
                >
                  <span className={`text-sm ${
                    isToday(day.date) ? 'text-[var(--primary)] font-bold' : 'text-white/70'
                  }`}>
                    {day.date.getDate()}
                  </span>
                  
                  <div className="mt-2 space-y-1">
                    {dayEvents.slice(0, 3).map((event, eventIndex) => (
                      <Link
                        key={event.id}
                        to={`/evento/${event.id}`}
                        className={`block text-xs px-2 py-1 truncate border-l-2 ${
                          genreColors[event.genre] || 'bg-white/10 text-white/70'
                        } hover:opacity-80 transition-opacity`}
                        title={event.title}
                      >
                        {event.time} {event.title}
                      </Link>
                    ))}
                    {dayEvents.length > 3 && (
                      <span className="text-xs text-white/50 px-2">
                        +{dayEvents.length - 3} más
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Week View */}
      {view === 'week' && (
        <div>
          <div className="grid grid-cols-7 border-b border-white/10">
            {getWeekDays().map(date => (
              <div 
                key={date.toISOString()} 
                className={`text-center py-4 ${isToday(date) ? 'bg-[var(--primary)]/10' : ''}`}
              >
                <div className="text-xs uppercase tracking-widest text-white/50">
                  {DAYS[date.getDay() === 0 ? 6 : date.getDay() - 1]}
                </div>
                <div className={`text-2xl font-['Playfair_Display'] mt-1 ${
                  isToday(date) ? 'text-[var(--primary)]' : 'text-white'
                }`}>
                  {date.getDate()}
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {getWeekDays().map(date => {
              const dayEvents = getEventsForDate(date);
              
              return (
                <div 
                  key={date.toISOString()} 
                  className="week-day-column p-2 min-h-[400px]"
                  data-testid={`week-day-${formatDateStr(date)}`}
                >
                  {dayEvents.map(event => (
                    <Link
                      key={event.id}
                      to={`/evento/${event.id}`}
                      className={`block mb-2 p-3 border-l-2 ${
                        genreColors[event.genre] || 'bg-white/10'
                      } hover:opacity-80 transition-opacity`}
                    >
                      <div className="text-xs text-white/50 mb-1">{event.time}</div>
                      <div className="text-sm font-medium text-white truncate">{event.title}</div>
                      <div className="text-xs text-white/50 mt-1 truncate">{event.venue}</div>
                    </Link>
                  ))}
                  {dayEvents.length === 0 && (
                    <div className="text-xs text-white/20 text-center mt-4">
                      Sin eventos
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="mt-8 pt-6 border-t border-white/10">
        <div className="flex flex-wrap gap-4">
          {Object.entries(genreColors).map(([genre, color]) => (
            <div key={genre} className="flex items-center gap-2">
              <div className={`w-3 h-3 ${color.split(' ')[0]}`} />
              <span className="text-xs text-white/50 capitalize">{genre}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
